import React from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Link } from 'gatsby';
import clsx from 'clsx';
import config from '../../config';
import { IUserGroup } from '../../apiHooks/userApi.types';
import ProfilePicture from '@components/atoms/ProfilePicture';

interface IProps {
    group: IUserGroup | null;
    asLink?: boolean;
    className?: string;
}

function GroupListItemContent({ group }: { group: IUserGroup }) {
    const { account } = group;

    return (
        <>
            <div className='flex-shrink-0'>
                <ProfilePicture
                    src={account.profile_picture_200x200}
                    accountHandle={account.handle}
                    className='size-9 rounded bg-slate-50 flex-shrink-0 border border-slate-100 mt-1'
                />
            </div>

            {/* Left side */}
            <div className='flex-1 w-full relative mb-5'>
                <p className='text-base text-gray-600 group-data-[focus]:text-white inline-flex items-center'>
                    <span className='font-semibold text-gray-900 group-data-[focus]:text-white'>
                        {account.display_name}
                    </span>
                </p>
                <p className='absolute w-full text-gray-600 group-data-[focus]:text-indigo-100 text-sm truncate'>
                    {account.description || account.handle}
                </p>
            </div>

            {/* Right side */}
            <div className='flex-none sm:w-42 flex items-center gap-x-1'>
                <ChevronRightIcon
                    aria-hidden='true'
                    className='size-5 flex-none text-gray-400 group-data-[focus]:text-white'
                />
            </div>
        </>
    );
}

export default function GroupListItem(props: IProps) {
    const { group, asLink = true, className } = props;

    const wrapperClassName = clsx(
        'h-16 flex flex-row overflow-hidden items-center justify-between py-0 gap-x-3 cursor-pointer hover:no-underline',
        'border-b border-gray-100',
        className ?? 'px-6',
    );

    if (!group) {
        return (
            <div className={wrapperClassName}>
                <div className='size-9 rounded bg-slate-50 flex-shrink-0 border border-slate-100 mt-1' />

                {/* Left side skeleton */}
                <div className='flex-1 w-full relative'>
                    {/* Title skeleton */}
                    <div className='inline-flex items-center'>
                        <div className='h-5 w-40 bg-gray-200 animate-pulse rounded' />
                    </div>
                    {/* Description skeleton */}
                    <div className='mt-1 h-4 w-full bg-gray-200 animate-pulse rounded' />
                </div>

                {/* Right side skeleton */}
                <div className='flex-none sm:w-42 flex items-center gap-x-1'>
                    <ChevronRightIcon aria-hidden='true' className='size-5 flex-none text-gray-400' />
                </div>
            </div>
        );
    }

    const { account } = group;
    const to = `/${config.resourcePrefix}${account.handle}/`;

    if (!asLink) {
        return (
            <div className={clsx(wrapperClassName, className ? 'rounded-md group-data-[focus]:bg-cyan-800' : 'hover:bg-gray-100')}>
                <GroupListItemContent group={group} />
            </div>
        );
    }

    return (
        <Link to={to} className={clsx(wrapperClassName, className ? 'rounded-md group-data-[focus]:bg-cyan-800' : 'hover:bg-gray-100')}>
            <GroupListItemContent group={group} />
        </Link>
    );
}
